import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  LayoutDashboard, Server, BarChart3, Users, TrendingUp, LogOut, Plus, Trash2, Edit2, 
  Save, X, Phone, MapPin, Award, Eye, Calendar, CheckCircle2,
  Settings, Layout, Image as ImageIcon, Star, ArrowUpAz, ChevronUp, ChevronDown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { services as staticServices, contactInfo } from '../data/services';
import LocationSelector from '../components/LocationSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [analytics, setAnalytics] = useState(null);
  const [servicePersons, setServicePersons] = useState([]);
  const [services, setServices] = useState(staticServices);
  const [selectedService, setSelectedService] = useState('all');
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [tempPriorities, setTempPriorities] = useState([]);
  const [siteSettings, setSiteSettings] = useState({ agents_online: 'true' });
  const [adminLocation, setAdminLocation] = useState('');
  
  // Person Form
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [formData, setFormData] = useState({
    name: '', phone: '', location: '', experience: '', is_available: true
  });

  // Service Management State
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceFormData, setServiceFormData] = useState({
    id: '', name: '', category: '', shortDesc: '', description: '', image: '', icon: '', rating: 4.5, parent_id: ''
  });

  // Slot & Booking State
  const [slots, setSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedSlotService, setSelectedSlotService] = useState('');
  const [slotFormData, setSlotFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: '10:00'
  });

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, [token]);

  const fetchServicePersons = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/service-persons`);
      setServicePersons(response.data);
    } catch (error) {
      console.error('Failed to fetch service persons:', error);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
      if (!selectedService && response.data.length > 0) setSelectedService('all');
      if (!selectedSlotService && response.data.length > 0) setSelectedSlotService(response.data[0].id);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  }, [selectedService, selectedSlotService]);

  const fetchSlots = useCallback(async (serviceId) => {
    try {
      const sid = serviceId || selectedSlotService;
      const response = await axios.get(`${API}/admin/slots?service_id=${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
    }
  }, [token, selectedSlotService]);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
      // Fetch all slots to resolve appointment times correctly across all services
      const slotsResponse = await axios.get(`${API}/admin/slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSlots(slotsResponse.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  }, [token]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchAnalytics(), 
        fetchServicePersons(),
        fetchServices(),
        fetchSlots(),
        fetchBookings(),
        fetchSettings()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchAnalytics, fetchServicePersons, fetchServices, fetchSlots, fetchBookings, fetchSettings]);

  const handleUpdateSettings = async (newSettings) => {
    try {
      await axios.patch(`${API}/admin/settings`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSettings();
      alert('Settings updated successfully!');
    } catch (error) {
      console.error('Failed to update settings:', error);
      alert('Failed to update settings');
    }
  };

  const handleSavePriorities = async () => {
    try {
      setLoading(true);
      // Priority is determined by position: index 0 = priority 1, index 1 = priority 2, etc.
      const payload = tempPriorities.map((item, idx) => ({
        id: item.id,
        priority: idx + 1
      }));
      await axios.put(`${API}/admin/services/priorities`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchServices();
      setShowPriorityModal(false);
      alert('Service order saved successfully!');
    } catch (error) {
      console.error('Failed to update priorities:', error);
      alert('Failed to update priorities');
    } finally {
      setLoading(false);
    }
  };

  const movePriorityItem = (fromIndex, toIndex) => {
    setTempPriorities(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  useEffect(() => {
    if (showPriorityModal && services.length > 0) {
      const initial = services
        .filter(s => !s.parent_id)
        .map(s => ({ id: s.id, name: s.name, priority: Number(s.priority) || 0 }))
        .sort((a, b) => a.priority - b.priority);
      setTempPriorities(initial);
    }
  }, [showPriorityModal]); // Only run when modal opens

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const handleToggleServiceStatus = async () => {
    if (selectedService === 'all') {
      alert('Please select a specific service to toggle its status.');
      return;
    }
    try {
      const response = await axios.patch(`${API}/admin/services/${selectedService}/status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setServices(services.map(s => 
        s.id === selectedService ? { ...s, is_active: response.data.is_active === 't' || response.data.is_active === true } : s
      ));
    } catch (error) {
      console.error('Failed to toggle service status:', error);
    }
  };

  const handleAddPerson = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/service-persons`, {
        service_id: selectedService,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddForm(false);
      setFormData({ name: '', phone: '', location: '', experience: '', is_available: true });
      fetchServicePersons();
    } catch (error) {
      console.error('Failed to add person:', error);
    }
  };

  const handleUpdatePerson = async (personId) => {
    try {
      await axios.put(`${API}/admin/service-persons/${personId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingPerson(null);
      setFormData({ name: '', phone: '', location: '', experience: '', is_available: true });
      fetchServicePersons();
    } catch (error) {
      console.error('Failed to update person:', error);
    }
  };

  const handleDeletePerson = async (personId) => {
    if (!window.confirm('Are you sure you want to delete this person?')) return;
    try {
      await axios.delete(`${API}/admin/service-persons/${personId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchServicePersons();
    } catch (error) {
      console.error('Failed to delete person:', error);
    }
  };

  const handleToggleStatus = async (personId) => {
    try {
      await axios.patch(`${API}/admin/service-persons/${personId}/status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchServicePersons();
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  const handleAssignPerson = async (bookingId, personId, customerName, serviceName, slotDate, slotTime) => {
    try {
      const response = await axios.put(`${API}/admin/bookings/${bookingId}/assign`, { person_id: personId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBookings();
      
      const { person_name } = response.data;
      const msg = `Hi ${customerName}, ${person_name} from our team is available and will reach out to you shortly for your ${serviceName} appointment. Contact ${contactInfo.phone} if needed.`;
      
      const customerPhone = bookings.find(b => b.id === bookingId)?.user_phone;
      if (customerPhone) {
        window.open(`https://wa.me/91${customerPhone.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`, '_blank');
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign person');
    }
  };

  const startEdit = (person) => {
    setEditingPerson(person.id);
    setFormData({
      name: person.name,
      phone: person.phone,
      location: person.location,
      experience: person.experience,
      is_available: person.is_available ?? true
    });
  };

  const cancelEdit = () => {
    setEditingPerson(null);
    setFormData({ name: '', phone: '', location: '', experience: '', is_available: true });
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/slots`, { ...slotFormData, service_id: selectedSlotService }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSlots();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add slot');
    }
  };

  const handleBulkAddSlots = async () => {
    if (!slotFormData.date) {
      alert('Please select a Date first!');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/admin/slots/bulk`, {
        service_id: selectedSlotService,
        date: slotFormData.date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(response.data.message || 'Slots generated successfully!');
      fetchSlots();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate bulk slots');
    } finally {
      setLoading(false);
    }
  };

  const handleUniversalBulkAddSlots = async () => {
    if (!slotFormData.date) {
      alert('Please select a Date first!');
      return;
    }
    if (!window.confirm(`This will generate 5 default slots (10AM-6PM) for ALL active services on ${slotFormData.date}. Continue?`)) return;
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/admin/slots/bulk`, {
        service_id: 'all',
        date: slotFormData.date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(response.data.message || 'Universal slots generated successfully!');
      fetchSlots();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate universal slots');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Delete this slot?')) return;
    try {
      await axios.delete(`${API}/admin/slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSlots();
    } catch (error) {
      console.error('Failed to delete slot:', error);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBookings();
    } catch (error) {
      console.error('Failed to delete booking:', error);
      alert(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/services`, serviceFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowServiceForm(false);
      setServiceFormData({ id: '', name: '', category: '', shortDesc: '', description: '', image: '', icon: '', rating: 4.5, parent_id: '' });
      fetchServices();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add service');
    }
  };

  const handleUpdateService = async (id) => {
    try {
      await axios.put(`${API}/admin/services/${id}`, serviceFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingService(null);
      setServiceFormData({ id: '', name: '', category: '', shortDesc: '', description: '', image: '', icon: '', rating: 4.5, parent_id: '' });
      fetchServices();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update service');
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Are you sure? This will delete the service. associated slots and persons may remain in DB but wont be bookable via UI.')) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchServices();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete service');
    }
  };

  const handleServiceImageUpload = async (e) => {
    const file = e.target.files[0];
    const targetId = e.target.id;
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      setLoading(true);
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const uploadedUrl = response.data.url;

      if (targetId === 'default-hero-upload') {
        handleUpdateSettings({ default_hero_image: uploadedUrl });
      } else {
        setServiceFormData(prev => ({
          ...prev,
          image: uploadedUrl
        }));
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startEditService = (service) => {
    setEditingService(service.id);
    setServiceFormData({ ...service });
  };

  const filteredPersons = servicePersons.filter(p => {
    const matchService = selectedService === 'all' || p.service_id === selectedService;
    const matchLocation = !adminLocation ||
      adminLocation === 'Detecting...' ||
      (p.location || '').toLowerCase().includes(adminLocation.toLowerCase());
    return matchService && matchLocation;
  });

  const filteredBookings = bookings.filter(b => {
    if (b.service_id !== selectedSlotService) return false;

    if (!adminLocation || adminLocation === 'Detecting...') return true;
    
    // Always show bookings that have no location specified so they aren't orphaned
    if (!b.user_location || b.user_location.trim() === '') return true;
    
    const locAdmin = adminLocation.toLowerCase();
    const locUser = b.user_location.toLowerCase();
    return locUser.includes(locAdmin) || locAdmin.includes(locUser);
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/assets/logo.svg" alt="VHFMS Logo" className="w-10 h-10 object-contain" />
              <div>
                <h1 className="text-lg font-bold text-slate-800 font-['Oswald']">VHFMS Dashboard</h1>
                <p className="text-xs text-slate-500">{contactInfo.brandName}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LocationSelector onLocationChange={(loc) => setAdminLocation(loc)} />
              <div className="h-8 w-px bg-slate-200 hidden sm:block"></div>
              <span className="text-sm text-slate-600">Welcome, {user?.name}</span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-slate-600 hover:text-red-500 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs - Scrollable on mobile */}
        <div className="flex gap-4 mb-8 overflow-x-auto pb-4 sm:pb-0 no-scrollbar">
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all shrink-0 whitespace-nowrap ${
              activeTab === 'analytics'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-100'
                : 'bg-white text-slate-600 hover:bg-blue-50'
            }`}
            data-testid="tab-analytics"
          >
            <BarChart3 size={20} />
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all shrink-0 whitespace-nowrap ${
              activeTab === 'services'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-100'
                : 'bg-white text-slate-600 hover:bg-blue-50'
            }`}
          >
            <Settings size={20} />
            Services
          </button>
          <button
            onClick={() => setActiveTab('persons')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all shrink-0 whitespace-nowrap ${
              activeTab === 'persons'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-100'
                : 'bg-white text-slate-600 hover:bg-blue-50'
            }`}
            data-testid="tab-persons"
          >
            <Users size={20} />
            Service Persons
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all shrink-0 whitespace-nowrap ${
              activeTab === 'bookings'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-100'
                : 'bg-white text-slate-600 hover:bg-blue-50'
            }`}
          >
            <Calendar size={20} />
            Slots & Bookings
          </button>
          <button
            onClick={() => setActiveTab('site-settings')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all shrink-0 whitespace-nowrap ${
              activeTab === 'site-settings'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-100'
                : 'bg-white text-slate-600 hover:bg-blue-50'
            }`}
          >
            <ImageIcon size={20} />
            Promotion Offer
          </button>
        </div>

        {/* Services Tab */}
        {activeTab === 'services' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold text-slate-800 font-['Oswald'] tracking-tight">Manage Services</h3>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowPriorityModal(true)}
                  className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-6 py-3 rounded-xl font-bold hover:bg-slate-50 transition-all shadow-sm"
                >
                  <ArrowUpAz size={18} />
                  Manage Priorities
                </button>
                <button
                  onClick={() => {
                    setEditingService(null);
                    setServiceFormData({ id: '', name: '', category: '', shortDesc: '', description: '', image: '', icon: '', rating: 4.5, parent_id: '' });
                    setShowServiceForm(!showServiceForm);
                  }}
                  className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-blue-200 transition-all active:scale-95"
                >
                  <Plus size={18} />
                  Add New Service
                </button>
              </div>
            </div>

            {showServiceForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-white p-6 rounded-2xl shadow-xl border border-blue-100"
              >
                 <form onSubmit={editingService ? () => handleUpdateService(editingService) : handleAddService} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase px-1">Service ID (URL-friendly)</label>
                      <input
                        type="text"
                        placeholder="e.g., deep-home-cleaning"
                        value={serviceFormData.id}
                        onChange={(e) => setServiceFormData({...serviceFormData, id: e.target.value})}
                        className="w-full px-4 py-3 bg-zinc-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                        required
                        disabled={editingService}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase px-1">Service Name</label>
                      <input
                        type="text"
                        placeholder="e.g., Deep Home Cleaning"
                        value={serviceFormData.name}
                        onChange={(e) => setServiceFormData({...serviceFormData, name: e.target.value})}
                        className="w-full px-4 py-3 bg-zinc-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase px-1">Category</label>
                      <input
                        type="text"
                        placeholder="e.g., Home Cleaning"
                        value={serviceFormData.category}
                        onChange={(e) => setServiceFormData({...serviceFormData, category: e.target.value})}
                        className="w-full px-4 py-3 bg-zinc-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                        required
                      />
                    </div>
                    <div className="lg:col-span-2 space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase px-1">Short Description</label>
                      <input
                        type="text"
                        placeholder="Brief summary for cards"
                        value={serviceFormData.shortDesc}
                        onChange={(e) => setServiceFormData({...serviceFormData, shortDesc: e.target.value})}
                        className="w-full px-4 py-3 bg-zinc-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase px-1">Icon Name (Lucide)</label>
                      <input
                        type="text"
                        placeholder="e.g., Home, Zap, Droplets"
                        value={serviceFormData.icon}
                        onChange={(e) => setServiceFormData({...serviceFormData, icon: e.target.value})}
                        className="w-full px-4 py-3 bg-zinc-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                        required
                      />
                    </div>
                    <div className="md:col-span-2 space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase px-1">Full Description</label>
                      <textarea
                        rows="3"
                        placeholder="Detailed explanation of the service..."
                        value={serviceFormData.description}
                        onChange={(e) => setServiceFormData({...serviceFormData, description: e.target.value})}
                        className="w-full px-4 py-3 bg-zinc-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none"
                        required
                      />
                    </div>
                     <div className="lg:col-span-1 space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase px-1">Service Image</label>
                      <div className="flex flex-col gap-2">
                        <div className="relative">
                          <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                          <input
                            type="text"
                            placeholder="Image URL (https://...)"
                            value={serviceFormData.image}
                            onChange={(e) => setServiceFormData({...serviceFormData, image: e.target.value})}
                            className="w-full pl-10 pr-4 py-3 bg-zinc-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            required
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            id="service-image-upload"
                            accept="image/*"
                            className="hidden"
                            onChange={handleServiceImageUpload}
                          />
                          <button
                            type="button"
                            onClick={() => document.getElementById('service-image-upload').click()}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl border border-blue-200 hover:bg-blue-100 transition-all font-bold text-xs"
                          >
                            <Plus size={14} />
                            Upload from Device
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase px-1">Parent Service (Optional)</label>
                      <select
                        value={serviceFormData.parent_id || ''}
                        onChange={(e) => setServiceFormData({...serviceFormData, parent_id: e.target.value || null})}
                        className="w-full px-4 py-3 bg-zinc-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                      >
                        <option value="">None (Top Level)</option>
                        {services.filter(s => s.id !== serviceFormData.id && !s.parent_id).map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2 lg:col-span-3 flex gap-3 pt-2">
                      <button type="submit" className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-lg shadow-blue-100">
                        {editingService ? 'Update Service' : 'Create Service'}
                      </button>
                      <button 
                        type="button" 
                        onClick={() => {
                          setShowServiceForm(false);
                          setEditingService(null);
                        }} 
                        className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-8 py-3 rounded-xl font-bold transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                 </form>
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map(svc => (
                <div key={svc.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all group">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-500 group-hover:bg-blue-500 group-hover:text-white transition-all">
                      <Layout size={24} />
                    </div>
                    <div className="flex gap-2">
                       <button onClick={() => {
                         startEditService(svc);
                         setShowServiceForm(true);
                         window.scrollTo({ top: 0, behavior: 'smooth' });
                       }} className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all">
                         <Edit2 size={16} />
                       </button>
                       <button onClick={() => handleDeleteService(svc.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                         <Trash2 size={16} />
                       </button>
                    </div>
                  </div>
                  <h4 className="font-bold text-slate-800 text-lg mb-1">{svc.name}</h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <p className="text-[10px] font-bold text-blue-500 uppercase bg-blue-50 px-2 py-0.5 rounded">{svc.category}</p>
                    {svc.parent_id && (
                      <p className="text-[10px] font-bold text-purple-500 uppercase bg-purple-50 px-2 py-0.5 rounded flex items-center gap-1">
                        <CheckCircle2 size={10} /> Sub-service
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-4">{svc.shortDesc}</p>
                  <div className="pt-4 border-t border-slate-50 flex items-center justify-between text-xs text-slate-400">
                    <span className="flex items-center gap-1 font-bold">
                      <Star size={12} className="text-yellow-500 fill-yellow-500" />
                      {svc.rating} Rating
                    </span>
                    <span>ID: {svc.id}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Priority Management Modal */}
            <AnimatePresence>
              {showPriorityModal && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm font-sans"
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden"
                  >
                    <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-slate-800 font-['Oswald'] flex items-center gap-2">
                          <ArrowUpAz className="text-blue-500" size={24} />
                          Manage Service Order
                        </h3>
                        <p className="text-sm text-slate-500">Use the arrows to reorder. Top = shows first on website.</p>
                      </div>
                      <button 
                        onClick={() => setShowPriorityModal(false)}
                        className="p-2 hover:bg-slate-200 rounded-full transition-colors"
                      >
                        <X size={20} className="text-slate-500" />
                      </button>
                    </div>

                    <div className="p-6 max-h-[60vh] overflow-y-auto space-y-2 custom-scrollbar">
                      {tempPriorities.map((s, idx) => (
                        <div key={s.id} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-2xl border border-slate-100 hover:border-blue-100 transition-all">
                          <div className="w-8 h-8 bg-blue-500 text-white rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-700 truncate text-sm">{s.name}</p>
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              onClick={() => movePriorityItem(idx, idx - 1)}
                              disabled={idx === 0}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            >
                              <ChevronUp size={16} className="text-blue-600" />
                            </button>
                            <button
                              onClick={() => movePriorityItem(idx, idx + 1)}
                              disabled={idx === tempPriorities.length - 1}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            >
                              <ChevronDown size={16} className="text-blue-600" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                      <button
                        onClick={() => setShowPriorityModal(false)}
                        className="px-6 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-200 transition-all font-sans"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSavePriorities}
                        className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg shadow-blue-100 transition-all flex items-center gap-2 font-sans"
                      >
                        <Save size={18} />
                        Save New Order
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Eye size={24} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{analytics.total_visits}</p>
                    <p className="text-sm text-slate-500">Total Visits</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <TrendingUp size={24} className="text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{analytics.today_visits}</p>
                    <p className="text-sm text-slate-500">Today's Visits</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users size={24} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{filteredPersons.length}</p>
                    <p className="text-sm text-slate-500">Service Persons</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Calendar size={24} className="text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{services.length}</p>
                    <p className="text-sm text-slate-500">Total Services</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Visits Chart */}
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h3 className="text-lg font-bold text-slate-800 mb-6 font-['Oswald']">Daily Visits (Last 7 Days)</h3>
              {analytics.daily_visits.length > 0 ? (
                <div className="h-64 flex items-end gap-4">
                  {analytics.daily_visits.map((day, index) => {
                    const maxVisits = Math.max(...analytics.daily_visits.map(d => d.visits), 1);
                    const height = (day.visits / maxVisits) * 100;
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">{day.visits}</span>
                        <div
                          className="w-full bg-blue-500 rounded-t-lg transition-all duration-300"
                          style={{ height: `${Math.max(height, 5)}%` }}
                        />
                        <span className="text-xs text-slate-500">{day.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  No visit data yet. Visits will appear here once users browse the website.
                </div>
              )}
            </div>

            {/* Visits by Service */}
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h3 className="text-lg font-bold text-slate-800 mb-6 font-['Oswald']">Visits by Service</h3>
              {Object.keys(analytics.visits_by_service).length > 0 ? (
                <div className="space-y-4">
                  {services.map((service) => {
                    const visits = analytics.visits_by_service[service.id] || 0;
                    const maxVisits = Math.max(...Object.values(analytics.visits_by_service), 1);
                    const width = (visits / maxVisits) * 100;
                    return (
                      <div key={service.id} className="flex items-center gap-4">
                        <div className="w-48 text-sm text-slate-600 truncate">{service.name}</div>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                          <div
                            className="bg-blue-500 h-full rounded-full transition-all duration-300"
                            style={{ width: `${Math.max(width, 2)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-slate-700 w-12 text-right">{visits}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-slate-400 py-8">
                  No service visits yet. Data will appear once users view service details.
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Service Persons Tab */}
        {activeTab === 'persons' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Service Selector */}
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <label className="block text-sm font-medium text-slate-700 mb-3">Select Service</label>
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="w-full md:w-96 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="service-selector"
              >
                <option value="all">All Services</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Service Status Toggle */}
            {selectedService !== 'all' && (
              <div className="bg-white p-6 rounded-2xl shadow-sm border-2 border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-800 font-['Oswald']">
                    Agent Status for: {services.find(s => s.id === selectedService)?.name}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {services.find(s => s.id === selectedService)?.is_active 
                      ? "Currently showing 'Technicians are active' on website" 
                      : "Currently showing 'Technicians are offline' on website"}
                  </p>
                </div>
                <button
                  onClick={handleToggleServiceStatus}
                  className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${
                    services.find(s => s.id === selectedService)?.is_active
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full ${services.find(s => s.id === selectedService)?.is_active ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                  {services.find(s => s.id === selectedService)?.is_active ? 'Technicians ONLINE' : 'Technicians OFFLINE'}
                </button>
              </div>
            )}

            {/* Add Person Button */}
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-800 font-['Oswald']">
                Persons for: {services.find(s => s.id === selectedService)?.name}
              </h3>
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl font-medium transition-colors"
                data-testid="add-person-btn"
              >
                <Plus size={18} />
                Add Person
              </button>
            </div>

            {/* Add Person Form */}
            {showAddForm && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-6 rounded-2xl shadow-sm border-2 border-blue-200"
              >
                <h4 className="font-semibold text-slate-800 mb-4">Add New Person</h4>
                <form onSubmit={handleAddPerson} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="person-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="person-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Location/Area</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="person-location-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Experience</label>
                    <input
                      type="text"
                      value={formData.experience}
                      onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                      placeholder="e.g., 5 years"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="person-experience-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Status</label>
                    <select
                      value={String(formData.is_available)}
                      onChange={(e) => setFormData({ ...formData, is_available: e.target.value === 'true' })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="true">Available</option>
                      <option value="false">Unavailable</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 flex gap-3 mt-2">
                    <button
                      type="submit"
                      className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                      data-testid="save-person-btn"
                    >
                      <Save size={18} />
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddForm(false);
                        setFormData({ name: '', phone: '', location: '', experience: '', is_available: true });
                      }}
                      className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-slate-600 px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                      <X size={18} />
                      Cancel
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Persons List */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {filteredPersons.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {filteredPersons.map((person) => (
                    <div key={person.id} className="p-6">
                      {editingPerson === person.id ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleUpdatePerson(person.id);
                          }}
                          className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        >
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="px-3 py-2 border border-slate-200 rounded-lg"
                            placeholder="Name"
                          />
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="px-3 py-2 border border-slate-200 rounded-lg"
                            placeholder="Phone"
                          />
                          <input
                            type="text"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                            className="px-3 py-2 border border-slate-200 rounded-lg"
                            placeholder="Location"
                          />
                          <input
                            type="text"
                            value={formData.experience}
                            onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                            className="px-3 py-2 border border-slate-200 rounded-lg"
                            placeholder="Experience"
                          />
                          <select
                            value={String(formData.is_available)}
                            onChange={(e) => setFormData({ ...formData, is_available: e.target.value === 'true' })}
                            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="true">Available</option>
                            <option value="false">Unavailable</option>
                          </select>
                          <div className="md:col-span-2 flex gap-2">
                            <button
                              type="submit"
                              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="bg-gray-100 hover:bg-gray-200 text-slate-600 px-4 py-2 rounded-lg text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                            <div>
                              <p className="text-xs text-slate-400 mb-1">Name</p>
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${person.is_available ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`} title={person.is_available ? 'Available' : 'Unavailable'} />
                                <p className="font-medium text-slate-800">{person.name}</p>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 mb-1">Phone</p>
                              <p className="text-slate-600 flex items-center gap-1">
                                <Phone size={14} className="text-blue-500" />
                                {person.phone}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 mb-1">Location</p>
                              <p className="text-slate-600 flex items-center gap-1">
                                <MapPin size={14} className="text-blue-500" />
                                {person.location}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 mb-1">Experience</p>
                              <p className="text-slate-600 flex items-center gap-1">
                                <Award size={14} className="text-blue-500" />
                                {person.experience}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleToggleStatus(person.id)}
                              className={`p-2 rounded-lg transition-colors border ${person.is_available ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
                              title={person.is_available ? "Mark Unavailable" : "Mark Available"}
                            >
                              <CheckCircle2 size={18} />
                            </button>
                            <button
                              onClick={() => startEdit(person)}
                              className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                              data-testid={`edit-person-${person.id}`}
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDeletePerson(person.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              data-testid={`delete-person-${person.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center text-slate-400">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No persons added for this service yet.</p>
                  <p className="text-sm">Click "Add Person" to add service providers.</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
        {/* Slots & Bookings Tab */}
        {activeTab === 'bookings' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Service Selector for Slots */}
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <label className="block text-sm font-medium text-slate-700 mb-3">Select Service for Slot Management</label>
              <select
                value={selectedSlotService}
                onChange={(e) => {
                  setSelectedSlotService(e.target.value);
                  fetchSlots(e.target.value);
                }}
                className="w-full md:w-96 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
            {/* Slot Management */}
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <h3 className="text-lg font-bold text-slate-800 mb-6 font-['Oswald'] flex items-center gap-2">
                  <Plus className="text-blue-500" size={20} />
                  Add Available Slot
                </h3>
                <form onSubmit={handleAddSlot} className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[150px]">
                    <label className="block text-xs text-slate-400 mb-1 uppercase font-bold">Date</label>
                    <input
                      type="date"
                      value={slotFormData.date}
                      onChange={(e) => setSlotFormData({...slotFormData, date: e.target.value})}
                      className="w-full px-4 py-2 bg-zinc-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      required
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-slate-400 mb-1 uppercase font-bold">Time</label>
                    <input
                      type="time"
                      value={slotFormData.time}
                      onChange={(e) => setSlotFormData({...slotFormData, time: e.target.value})}
                      className="w-full px-4 py-2 bg-zinc-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      required
                    />
                  </div>
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-bold transition-all text-sm"
                    >
                      Add This Single Slot
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkAddSlots}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 px-4 rounded-lg font-bold transition-all flex items-center justify-center gap-2 text-sm"
                      title="Generates 6 default slots for THIS service"
                    >
                      <Plus size={16} />
                      Generate 6 for THIS Service
                    </button>
                    <button
                      type="button"
                      onClick={handleUniversalBulkAddSlots}
                      className="md:col-span-2 bg-emerald-500 hover:bg-emerald-600 text-white py-3 px-4 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
                      title="Generates 6 default slots for ALL active services at once"
                    >
                      <Plus size={18} />
                      1-Click Generate for ALL Services (Today/Selected)
                    </button>
                  </div>
                </form>
              </div>

              <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-100">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-bold text-slate-800 font-['Oswald'] flex items-center gap-2">
                    <Calendar className="text-blue-500" size={18} />
                    All Slots
                  </h3>
                  <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-md">{slots.length} Total</span>
                </div>
                <div className="max-h-[500px] overflow-y-auto divide-y divide-slate-100 pb-2 custom-scrollbar">
                  {slots.filter(s => !slotFormData.date || s.date === slotFormData.date).length > 0 ? 
                    slots
                      .filter(s => !slotFormData.date || s.date === slotFormData.date)
                      .sort((a, b) => {
                        const dateCompare = new Date(a.date) - new Date(b.date);
                        if (dateCompare !== 0) return dateCompare;
                        return a.time.localeCompare(b.time);
                      })
                      .map((slot) => (
                    <div key={slot.id} className="p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className={`w-2 h-2 rounded-full ${slot.is_available ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`} title={slot.is_available ? 'Available' : 'Booked'} />
                        <div>
                          <p className="font-bold text-slate-800">{new Date(slot.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1 font-medium">
                            {slot.time}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteSlot(slot.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )) : (
                    <div className="p-12 text-center text-slate-400">
                      <Calendar size={32} className="mx-auto mb-2 opacity-20" />
                      <p className="text-sm">No slots for {slotFormData.date || 'selected date'}.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bookings View */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col border border-slate-100">
              <div className="p-6 border-b border-slate-100 bg-zinc-50/50">
                <h3 className="text-lg font-bold text-slate-800 font-['Oswald'] flex items-center gap-2">
                  <Users className="text-emerald-500" size={20} />
                  Customer Bookings
                </h3>
              </div>
              <div className="flex-1 max-h-[750px] overflow-y-auto p-6 space-y-4 custom-scrollbar">
                {filteredBookings.length > 0 ? filteredBookings.map((booking) => {
                  const s = services.find(sv => sv.id === booking.service_id);
                  const slot = allSlots.find(sl => sl.id === booking.slot_id);
                  return (
                    <div key={booking.id} className="p-5 border border-slate-100 rounded-2xl hover:border-blue-200 transition-all shadow-sm bg-white hover:shadow-md">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <h4 className="font-bold text-slate-800 text-lg">{booking.user_name}</h4>
                            <button
                              onClick={() => handleDeleteBooking(booking.id)}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                              title="Delete Booking"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <p className="text-blue-600 font-bold flex items-center gap-1 text-sm bg-blue-50 px-2 py-0.5 rounded-lg w-fit mt-1">
                            <Phone size={14} /> {booking.user_phone}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-0.5">Booked On</p>
                          <p className="text-xs font-medium text-slate-500 bg-slate-50 px-2 py-1 rounded">
                            {new Date(booking.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 bg-zinc-50 p-4 rounded-xl border border-dotted border-slate-200">
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase font-bold mb-1">Service Required</p>
                          <p className="text-sm font-semibold text-slate-700 truncate">{s?.name || 'Unknown Service'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase font-bold mb-1">Appointment Time</p>
                          <p className="text-sm font-semibold text-slate-700">
                            {slot ? `${new Date(slot.date).toLocaleDateString('en-GB')} @ ${slot.time}` : 'Slot info missing'}
                          </p>
                        </div>
                      </div>

                      {/* Assignment Section */}
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        {booking.status === 'assigned' && booking.assigned_person_id ? (
                          <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm flex items-center justify-between">
                            <div className="flex items-center gap-2 font-medium">
                              <CheckCircle2 size={16} />
                              Assigned to: {servicePersons.find(p => p.id === booking.assigned_person_id)?.name || 'Unknown'}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <select
                              id={`assign-${booking.id}`}
                              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-zinc-50 outline-none focus:ring-2 focus:ring-blue-500"
                              defaultValue=""
                            >
                              <option value="" disabled>Select available person...</option>
                              {servicePersons
                                .filter(p => p.service_id === booking.service_id && p.is_available !== false && (!adminLocation || adminLocation === 'Detecting...' || (p.location || '').toLowerCase().includes(adminLocation.toLowerCase())))
                                .map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))
                              }
                            </select>
                            <button
                              onClick={() => {
                                const selectEl = document.getElementById(`assign-${booking.id}`);
                                if (!selectEl.value) return alert('Please select a person first.');
                                handleAssignPerson(
                                  booking.id,
                                  selectEl.value,
                                  booking.user_name,
                                  s?.name || 'Service',
                                  slot?.date,
                                  slot?.time
                                );
                              }}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                            >
                              Assign
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }) : (
                  <div className="py-20 text-center text-slate-400">
                    <Users size={48} className="mx-auto mb-4 opacity-10" />
                    <p>No bookings received yet.</p>
                  </div>
                )}
              </div>
            </div>
            </div>
          </motion.div>
        )}

        {/* Promotion Offer Tab */}
        {activeTab === 'site-settings' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 max-w-4xl mx-auto font-sans">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 font-['Oswald'] flex items-center gap-2">
                    <ImageIcon className="text-blue-500" size={28} />
                    Promotion Carousel Management
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">Add multiple promotions to cycle through on the homepage.</p>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Left: Add New Section */}
                <div className="space-y-6 lg:border-r border-slate-100 lg:pr-12">
                  <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100">
                    <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <Plus size={18} className="text-blue-500" />
                      Add New Promotion
                    </h4>
                    
                    <div className="space-y-4">
                      {/* Device Upload - Converts to Base64 for offline reliability */}
                      <div className="p-4 bg-white rounded-xl border-2 border-dashed border-blue-200 hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          id="new-promo-upload"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (file.size > 2 * 1024 * 1024) {
                              alert('Image is too large (max 2MB). Please resize or use a URL instead.');
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = () => {
                              let currentPromos = [];
                              try { currentPromos = JSON.parse(siteSettings.promotions_list || '[]'); } catch(e) {}
                              const newPromo = {
                                id: Math.random().toString(36).substr(2, 9),
                                url: reader.result,
                                expiry: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
                              };
                              handleUpdateSettings({ promotions_list: JSON.stringify([...currentPromos, newPromo]) });
                            };
                            reader.readAsDataURL(file);
                          }}
                        />
                        <button 
                          onClick={() => document.getElementById('new-promo-upload').click()}
                          className="w-full flex flex-col items-center gap-2 text-blue-600 font-bold"
                        >
                          <ImageIcon size={32} />
                          <span>Upload Image from Device</span>
                          <span className="text-[10px] text-slate-400 font-normal">Max 2MB · Stored securely in database (always loads)</span>
                        </button>
                      </div>

                      {/* URL Input Option */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className="text-xs text-slate-400 font-medium shrink-0">or paste a URL</span>
                        <div className="flex-1 h-px bg-slate-200" />
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          id="new-promo-url"
                          placeholder="https://example.com/image.jpg"
                          className="flex-1 text-sm px-3 py-2.5 border border-slate-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <button
                          onClick={() => {
                            const urlInput = document.getElementById('new-promo-url');
                            const url = urlInput?.value?.trim();
                            if (!url) { alert('Please enter a valid image URL.'); return; }
                            let currentPromos = [];
                            try { currentPromos = JSON.parse(siteSettings.promotions_list || '[]'); } catch(e) {}
                            const newPromo = {
                              id: Math.random().toString(36).substr(2, 9),
                              url,
                              expiry: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
                            };
                            handleUpdateSettings({ promotions_list: JSON.stringify([...currentPromos, newPromo]) });
                            urlInput.value = '';
                          }}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors shrink-0"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Default Image Management */}
                  <div className="p-5 bg-amber-50 rounded-2xl border border-amber-200 space-y-4">
                    <div className="flex items-center gap-2">
                      <Star size={16} className="text-amber-500" />
                      <h4 className="font-bold text-slate-800 text-sm">Default Hero Image</h4>
                    </div>
                    <p className="text-xs text-slate-500">This image shows when no active promotions are available.</p>

                    {/* Current Default Preview */}
                    {siteSettings.default_hero_image && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-amber-200">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                          <img src={siteSettings.default_hero_image} alt="Default" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-700">Current Default</p>
                          <p className="text-[10px] text-slate-400 truncate">
                            {siteSettings.default_hero_image.startsWith('data:') ? 'Uploaded from device' : siteSettings.default_hero_image}
                          </p>
                        </div>
                        <button
                          onClick={() => handleUpdateSettings({ default_hero_image: null })}
                          className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-all"
                          title="Remove default image"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}

                    {/* Device Upload for Default Image */}
                    <div>
                      <input
                        type="file"
                        id="default-hero-upload"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (file.size > 2 * 1024 * 1024) {
                            alert('Image is too large (max 2MB). Please resize or use a URL instead.');
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () => {
                            handleUpdateSettings({ default_hero_image: reader.result });
                          };
                          reader.readAsDataURL(file);
                        }}
                      />
                      <button
                        onClick={() => document.getElementById('default-hero-upload').click()}
                        className="w-full flex items-center justify-center gap-2 bg-white border border-dashed border-amber-300 hover:border-amber-500 text-amber-700 font-semibold text-sm py-2.5 rounded-xl transition-colors"
                      >
                        <ImageIcon size={16} />
                        Upload from Device
                      </button>
                    </div>

                    {/* URL Input for Default Image */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-px bg-amber-200" />
                      <span className="text-[10px] text-slate-400 font-medium shrink-0">or paste a URL</span>
                      <div className="flex-1 h-px bg-amber-200" />
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        id="default-hero-url"
                        placeholder="https://example.com/image.jpg"
                        className="flex-1 text-sm px-3 py-2 border border-amber-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-amber-400"
                      />
                      <button
                        onClick={() => {
                          const urlInput = document.getElementById('default-hero-url');
                          const url = urlInput?.value?.trim();
                          if (!url) { alert('Please enter a valid image URL.'); return; }
                          handleUpdateSettings({ default_hero_image: url });
                          urlInput.value = '';
                        }}
                        className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shrink-0"
                      >
                        Set
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right: Active Promotions List */}
                <div className="space-y-4">
                  <h4 className="font-bold text-slate-700 uppercase text-xs tracking-widest flex items-center justify-between">
                    Current Active Promotions
                    <span className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded text-[10px]">
                      {(JSON.parse(siteSettings.promotions_list || '[]')).length} active
                    </span>
                  </h4>
                  
                  <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                    {(JSON.parse(siteSettings.promotions_list || '[]')).map((promo) => (
                      <div key={promo.id} className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm hover:border-blue-300 transition-all group">
                        <div className="flex gap-4">
                          <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                            <img 
                              src={promo.url} 
                              alt="Promo" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 space-y-2">
                            <div>
                              <p className="text-[9px] font-bold text-slate-400 uppercase">Expiry Date</p>
                              <input 
                                type="datetime-local"
                                value={promo.expiry}
                                onChange={(e) => {
                                  const list = JSON.parse(siteSettings.promotions_list);
                                  const updated = list.map(p => p.id === promo.id ? { ...p, expiry: e.target.value } : p);
                                  handleUpdateSettings({ promotions_list: JSON.stringify(updated) });
                                }}
                                className="w-full text-xs font-semibold bg-zinc-50 border border-slate-100 px-2 py-1 rounded focus:ring-1 focus:ring-blue-500 font-sans"
                              />
                            </div>
                            <div className="flex items-center justify-between pt-1">
                              {new Date(promo.expiry) < new Date() ? (
                                <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold">EXPIRED</span>
                              ) : (
                                <span className="text-[9px] bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full font-bold">ACTIVE</span>
                              )}
                              <button 
                                onClick={() => {
                                  const list = JSON.parse(siteSettings.promotions_list);
                                  handleUpdateSettings({ promotions_list: JSON.stringify(list.filter(p => p.id !== promo.id)) });
                                }}
                                className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {(JSON.parse(siteSettings.promotions_list || '[]')).length === 0 && (
                      <div className="py-12 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                        <ImageIcon size={32} className="mx-auto mb-2 opacity-20" />
                        <p className="text-sm">No active promotions. Only default image will show.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

